import { useState, useEffect, useCallback, useRef } from 'react';
import { money } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';
const LS = 'tk_moza_sesion';

// MOZAS — pantalla web (no APK) para que las mozas reciban los pedidos de las mesas,
// los lleven, los cobren y rindan al cierre.
//
// Es web a propósito: el TPV es un APK y cada cambio obliga a recompilar e instalar en
// cada equipo. Una moza entra con el link desde su propio teléfono.
//
// Abre sesión igual que una caja del TPV (evento + caja + PIN) porque la moza ES una
// caja: cobra la diferencia cuando el saldo de la mesa no alcanza, y después rinde.
export default function Mozas() {
  const [sesion, setSesion] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS) || 'null'); } catch { return null; }
  });

  const salir = () => { localStorage.removeItem(LS); setSesion(null); };
  const entrar = (s) => { localStorage.setItem(LS, JSON.stringify(s)); setSesion(s); };

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '24px 16px 60px' }}>
      {!sesion ? <Ingreso onEntrar={entrar} /> : <Pedidos sesion={sesion} onSalir={salir} />}
    </div>
  );
}

// ───────────── Ingreso ─────────────

function Ingreso({ onEntrar }) {
  const [eventos, setEventos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [f, setF] = useState({ event_id: '', box_id: '', pin: '' });
  const [err, setErr] = useState('');
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/tpv/public/events`).then(r => r.json())
      .then(d => setEventos(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!f.event_id) { setCajas([]); return; }
    // kind=moza: la lista trae solo las mozas, no las barras ni la boletería.
    fetch(`${API}/api/tpv/public/boxes?event_id=${f.event_id}&kind=moza`).then(r => r.json())
      .then(d => setCajas(Array.isArray(d) ? d : [])).catch(() => {});
  }, [f.event_id]);

  async function abrir(e) {
    e.preventDefault();
    setErr(''); setCargando(true);
    try {
      const r = await fetch(`${API}/api/auth/tpv-open`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'No se pudo abrir'); return; }
      onEntrar({ token: d.token, ...d.session });
    } catch {
      setErr('Sin conexión. Probá de nuevo.');
    } finally { setCargando(false); }
  }

  return (
    <form onSubmit={abrir}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Servicio a la mesa</div>
      <h1 style={{ fontSize: 22, color: '#fff', margin: '0 0 20px' }}>Mozas</h1>

      <label className="lb-label">Evento</label>
      <select className="lb-input" value={f.event_id}
        onChange={e => setF({ ...f, event_id: e.target.value, box_id: '' })} required>
        <option value="">Elegí el evento</option>
        {eventos.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>

      <label className="lb-label" style={{ marginTop: 12 }}>Tu nombre</label>
      <select className="lb-input" value={f.box_id}
        onChange={e => setF({ ...f, box_id: e.target.value })} required disabled={!f.event_id}>
        <option value="">{f.event_id ? 'Elegí tu nombre' : 'Elegí primero el evento'}</option>
        {cajas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {f.event_id && !cajas.length && (
        <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 6, lineHeight: 1.45 }}>
          Este evento no tiene mozas cargadas. Que las den de alta desde el panel, en Cajas.
        </div>
      )}

      <label className="lb-label" style={{ marginTop: 12 }}>PIN</label>
      <input className="lb-input" type="tel" inputMode="numeric" value={f.pin}
        onChange={e => setF({ ...f, pin: e.target.value })} placeholder="••••" required />

      {err && <div className="lb-err" style={{ marginTop: 12 }}>{err}</div>}
      <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 18 }} disabled={cargando}>
        {cargando ? 'Abriendo…' : 'Entrar'}
      </button>
    </form>
  );
}

// ───────────── Pedidos ─────────────

function Pedidos({ sesion, onSalir }) {
  const [pedidos, setPedidos] = useState([]);
  const [err, setErr] = useState('');
  const [ocupado, setOcupado] = useState('');       // id del pedido en acción
  const [verRendicion, setVerRendicion] = useState(false);
  const previos = useRef(new Set());
  const [nuevos, setNuevos] = useState(0);

  const auth = useCallback((url, opts = {}) => fetch(`${API}${url}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sesion.token}`, ...(opts.headers || {}) },
  }), [sesion.token]);

  const cargar = useCallback(async () => {
    try {
      const r = await auth('/api/tpv/pedidos');
      if (r.status === 401) { onSalir(); return; }
      const d = await r.json();
      const lista = Array.isArray(d) ? d : [];
      // Marcar los que entraron desde la última vuelta: con el teléfono en el bolsillo,
      // lo único que avisa es ver el contador al sacarlo.
      const ids = new Set(lista.map(p => p.id));
      const recien = lista.filter(p => p.status === 'pendiente' && !previos.current.has(p.id)).length;
      if (previos.current.size) setNuevos(n => n + recien);
      previos.current = ids;
      setPedidos(lista);
      setErr('');
    } catch {
      setErr('Sin conexión: la lista puede estar desactualizada.');
    }
  }, [auth, onSalir]);

  // 10 s: la mesa está esperando y la moza necesita enterarse rápido.
  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 10000);
    return () => clearInterval(id);
  }, [cargar]);

  async function accion(id, ruta, body) {
    setOcupado(id); setErr('');
    try {
      const r = await auth(`/api/tpv/pedidos/${id}/${ruta}`, { method: 'POST', body: JSON.stringify(body || {}) });
      const d = await r.json();
      if (!r.ok) setErr(d.error || 'No se pudo');
      await cargar();
    } catch {
      setErr('Sin conexión: no se pudo registrar. Reintentá.');
    } finally { setOcupado(''); }
  }

  const pendientes = pedidos.filter(p => p.status === 'pendiente');
  const mios = pedidos.filter(p => p.status === 'tomado');

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div className="lb-eyebrow">{sesion.eventName}</div>
          <div style={{ fontSize: 19, color: '#fff', fontWeight: 600 }}>{sesion.boxName}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setVerRendicion(true)}>Mi caja</button>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onSalir}>Salir</button>
        </div>
      </div>

      {err && <div className="lb-err" style={{ marginBottom: 12 }}>{err}</div>}

      {nuevos > 0 && (
        <button className="lb-btn lb-btn--white lb-btn--block" style={{ marginBottom: 14 }} onClick={() => setNuevos(0)}>
          {nuevos === 1 ? '1 pedido nuevo' : `${nuevos} pedidos nuevos`} · tocá para marcar como visto
        </button>
      )}

      {mios.length > 0 && (
        <Seccion titulo="Los estás llevando">
          {mios.map(p => (
            <Pedido key={p.id} p={p} ocupado={ocupado === p.id}
              onEntregar={(metodo) => accion(p.id, 'entregar', { cobro_metodo: metodo })}
              onCancelar={(motivo) => accion(p.id, 'cancelar', { reason: motivo })} />
          ))}
        </Seccion>
      )}

      <Seccion titulo="Esperando">
        {pendientes.map(p => (
          <Pedido key={p.id} p={p} ocupado={ocupado === p.id}
            onTomar={() => accion(p.id, 'tomar')} />
        ))}
        {!pendientes.length && (
          <div style={{ padding: '18px 0', color: 'var(--ink-600)', fontSize: 13 }}>
            No hay pedidos esperando.
          </div>
        )}
      </Seccion>

      {verRendicion && <Rendicion auth={auth} onCerrar={() => setVerRendicion(false)} />}
    </>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 10 }}>{titulo}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Pedido({ p, ocupado, onTomar, onEntregar, onCancelar }) {
  const [cobrando, setCobrando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const aCobrar = Number(p.a_cobrar) || 0;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
          {p.mesa}{p.sector && <span style={{ fontWeight: 400, color: 'var(--ink-600)', fontSize: 12 }}> · {p.sector}</span>}
        </div>
        <span style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>#{p.numero} · {hora(p.created_at)}</span>
      </div>

      <div style={{ margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {(p.items || []).map((i, n) => (
          <div key={n} style={{ fontSize: 14, color: '#fff' }}>
            <b>{i.qty}×</b> {i.name}
          </div>
        ))}
      </div>

      {p.nota && (
        <div style={{ fontSize: 12.5, color: '#ffb300', marginBottom: 8 }}>📝 {p.nota}</div>
      )}

      {/* Lo que tiene que cobrar es lo primero que necesita ver: si dice "cobrar $0",
          va y entrega sin pedir plata. */}
      <div style={{ padding: '9px 12px', background: 'var(--ink-050)', borderRadius: 6, marginBottom: 10 }}>
        {aCobrar > 0
          ? <div style={{ fontSize: 14, color: '#fff' }}>Cobrar <b style={{ fontSize: 17 }}>{money(aCobrar)}</b>
              {Number(p.pagado_saldo) > 0 && <span style={{ color: 'var(--ink-600)', fontSize: 12 }}> · {money(p.pagado_saldo)} salió del saldo</span>}
            </div>
          : <div style={{ fontSize: 14, color: '#2ecc71' }}>No cobrás nada · sale todo del saldo de la mesa</div>}
      </div>

      {onTomar && (
        <button className="lb-btn lb-btn--primary lb-btn--block" disabled={ocupado} onClick={onTomar}>
          {ocupado ? 'Tomando…' : 'Lo llevo yo'}
        </button>
      )}

      {onEntregar && !cobrando && !cancelando && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="lb-btn lb-btn--primary" style={{ flex: 1 }} disabled={ocupado}
            onClick={() => (aCobrar > 0 ? setCobrando(true) : onEntregar(null))}>
            {ocupado ? 'Guardando…' : 'Entregado'}
          </button>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setCancelando(true)}>Cancelar</button>
        </div>
      )}

      {cobrando && (
        <div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginBottom: 8 }}>¿Cómo te pagaron los {money(aCobrar)}?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="lb-btn lb-btn--primary" style={{ flex: 1 }} disabled={ocupado} onClick={() => onEntregar('efectivo')}>Efectivo</button>
            <button className="lb-btn lb-btn--white" style={{ flex: 1 }} disabled={ocupado} onClick={() => onEntregar('transferencia')}>Transferencia</button>
          </div>
          <button className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" style={{ marginTop: 6 }} onClick={() => setCobrando(false)}>Volver</button>
        </div>
      )}

      {cancelando && (
        <div>
          <input className="lb-input" value={motivo} onChange={e => setMotivo(e.target.value)}
            placeholder="¿Por qué se cancela? (se devuelve el saldo)" />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="lb-btn lb-btn--danger" style={{ flex: 1 }} disabled={!motivo.trim() || ocupado}
              onClick={() => onCancelar(motivo)}>Cancelar el pedido</button>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { setCancelando(false); setMotivo(''); }}>Volver</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Rendicion({ auth, onCerrar }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    auth('/api/tpv/pedidos/rendicion').then(r => r.json()).then(setD).catch(() => setD({ error: true }));
  }, [auth]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', zIndex: 50 }}
      onClick={onCerrar}>
      <div style={{ background: 'var(--surface)', width: '100%', borderRadius: '12px 12px 0 0', padding: '20px 20px 26px' }}
        onClick={e => e.stopPropagation()}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Mi caja</div>
        {!d && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}
        {d?.error && <div className="lb-err">No se pudo cargar.</div>}
        {d && !d.error && (
          <>
            <L t={`Pedidos entregados`} v={d.pedidos} />
            <L t="Servido" v={money(d.servido)} />
            {d.conSaldo > 0 && <L t="Pagado con saldo de mesa" v={`− ${money(d.conSaldo)}`} tenue />}
            <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
            <L t="Efectivo" v={money(d.efectivo)} />
            <L t="Transferencia" v={money(d.transferencia)} />
            <L t="Tenés que entregar" v={money(d.aRendir)} fuerte />
            {/* El saldo de mesa ya se cobró cuando se vendió la mesa: si no se aclara,
                la moza cree que le falta plata. */}
            {d.conSaldo > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 10, lineHeight: 1.45 }}>
                Lo que salió del saldo de las mesas ya estaba pago: no entra en lo que entregás.
              </div>
            )}
          </>
        )}
        <button className="lb-btn lb-btn--white lb-btn--block" style={{ marginTop: 18 }} onClick={onCerrar}>Cerrar</button>
      </div>
    </div>
  );
}

function L({ t, v, fuerte, tenue }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>{t}</span>
      <span style={{ fontSize: fuerte ? 18 : 14, fontWeight: fuerte ? 700 : 500, color: tenue ? 'var(--ink-600)' : '#fff' }}>{v}</span>
    </div>
  );
}

function hora(s) {
  try { return new Date(s).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }); } catch { return ''; }
}
